import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { setAuthToken } from '../lib/api';
import { levelAllows } from '../lib/modules';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // `booting` gates route guards while we resolve the existing session (cookie).
  const [booting, setBooting] = useState(true);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* best-effort */ }
    setAuthToken(null);
    setUser(null);
  }, []);

  // Resolve the session from the httpOnly cookie: /me authenticates via the
  // cookie, so group/permission changes take effect on the next load.
  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get('/me');
      setUser(data);
    } catch (e) {
      if (e.response && [401, 403].includes(e.response.status)) {
        setAuthToken(null);
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    (async () => { await refreshMe(); setBooting(false); })();
  }, [refreshMe]);

  // Permission helper derived from the resolved group matrix.
  const can = useCallback((module, level = 'read') => {
    const lvl = user?.permissions?.[module] || 'deny';
    return levelAllows(lvl, level);
  }, [user]);

  const scope = user?.scope || 'own';

  return (
    <AuthContext.Provider value={{ user, loading: booting, booting, login, logout, refreshMe, can, scope }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
